import React, { useState, useEffect } from 'react';
import QuestionCard from './components/QuestionCard';
import ProgressBar from './components/ProgressBar';
import ResultsScreen from './components/ResultsScreen';
import DimensionSelector from './components/DimensionSelector';
import { questions, dimensionGroups } from './data/questions';

function App() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [results, setResults] = useState(null);
  const [showDimensionSelector, setShowDimensionSelector] = useState(true);
  const [selectedDimension, setSelectedDimension] = useState(null);
  const [filteredQuestions, setFilteredQuestions] = useState([]);
  const [isExitModalOpen, setIsExitModalOpen] = useState(false);

  // Load saved progress from localStorage if available
  useEffect(() => {
    const savedProgress = localStorage.getItem('bumnTestProgress');
    if (savedProgress) {
      const { 
        answers: savedAnswers, 
        currentIndex: savedIndex,
        selectedDimension: savedDimension 
      } = JSON.parse(savedProgress);
      
      setAnswers(savedAnswers);
      setCurrentIndex(savedIndex);
      
      if (savedDimension) {
        setSelectedDimension(savedDimension);
        filterQuestionsByDimension(savedDimension);
        setShowDimensionSelector(false);
      }
    }
  }, []);

  // Filter questions based on selected dimension
  const filterQuestionsByDimension = (dimensionId) => {
    if (!dimensionId) {
      setFilteredQuestions(questions);
      return;
    }
    
    const questionIds = dimensionGroups[dimensionId];
    const filtered = questions.filter(question => questionIds.includes(question.id));
    setFilteredQuestions(filtered);
  };

  // Save progress to localStorage whenever answers, currentIndex, or selectedDimension changes
  useEffect(() => {
    if (Object.keys(answers).length > 0) {
      localStorage.setItem('bumnTestProgress', JSON.stringify({ 
        answers, 
        currentIndex,
        selectedDimension 
      }));
    }
  }, [answers, currentIndex, selectedDimension]);

  const handleAnswer = (questionId, optionIndex) => {
    setAnswers(prev => ({ ...prev, [questionId]: optionIndex }));
    
    // Move to next question if not at the end
    const activeQuestions = selectedDimension ? filteredQuestions : questions;
    if (currentIndex < activeQuestions.length - 1) {
      setCurrentIndex(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
    }
  };

  const handleDimensionSelect = (dimensionId) => {
    setSelectedDimension(dimensionId);
    filterQuestionsByDimension(dimensionId);
    setCurrentIndex(0);
    setAnswers({});
    setShowDimensionSelector(false);
  };

  const handleStartFullTest = () => {
    setSelectedDimension(null);
    setFilteredQuestions(questions);
    setCurrentIndex(0);
    setAnswers({});
    setShowDimensionSelector(false);
  };

  const handleSubmit = () => {
    // Calculate results
    const results = calculateResults(answers);
    setResults(results);
    setShowResults(true);
    
    // Clear local storage after submission
    localStorage.removeItem('bumnTestProgress');
  };

  const calculateResults = (answers) => {
    // Pemetaan dari dimensionTrait ke dimensi
    const traitToDimension = {
      'structure': 'structureVsFlexibility',
      'flexibility': 'structureVsFlexibility',
      'introvert': 'introvertVsExtrovert',
      'extrovert': 'introvertVsExtrovert',
      'practical': 'practicalVsConceptual',
      'conceptual': 'practicalVsConceptual',
      'risk': 'riskVsCaution',
      'caution': 'riskVsCaution',
      'independent': 'independentVsCollaborative',
      'collaborative': 'independentVsCollaborative'
    };

    // Konfigurasi trait yang menjadi skor positif dalam setiap dimensi
    const positiveTrait = {
      'structureVsFlexibility': 'structure',
      'introvertVsExtrovert': 'introvert',
      'practicalVsConceptual': 'practical',
      'riskVsCaution': 'caution',
      'independentVsCollaborative': 'independent'
    };

    // Initialize scores for each dimension
    const dimensionScores = {};
    const dimensionConsistency = {};
    const traitCounts = {};
    
    // Initialize all dimensions or just the selected one
    if (selectedDimension) {
      dimensionScores[selectedDimension] = 0;
      dimensionConsistency[selectedDimension] = 0;
      
      // Initialize trait counts for the selected dimension
      const positiveTraitKey = positiveTrait[selectedDimension];
      const negativeTraitKey = selectedDimension === 'structureVsFlexibility' ? 'flexibility' : 
                               selectedDimension === 'introvertVsExtrovert' ? 'extrovert' :
                               selectedDimension === 'practicalVsConceptual' ? 'conceptual' :
                               selectedDimension === 'riskVsCaution' ? 'risk' : 'collaborative';
      
      traitCounts[positiveTraitKey] = 0;
      traitCounts[negativeTraitKey] = 0;
    } else {
      Object.keys(dimensionGroups).forEach(dimension => {
        dimensionScores[dimension] = 0;
        dimensionConsistency[dimension] = 0;
      });
      
      // Initialize all trait counts
      Object.values(positiveTrait).forEach(trait => {
        traitCounts[trait] = 0;
      });
      traitCounts['flexibility'] = 0;
      traitCounts['extrovert'] = 0;
      traitCounts['conceptual'] = 0;
      traitCounts['risk'] = 0;
      traitCounts['collaborative'] = 0;
    }
    
    // Calculate raw scores for each dimension based on metadata
    Object.entries(answers).forEach(([questionId, answerIndex]) => {
      const qId = parseInt(questionId);
      const question = questions.find(q => q.id === qId);
      
      if (question && question.metadata && question.metadata[answerIndex]) {
        const selectedTrait = question.metadata[answerIndex].dimensionTrait;
        const dimension = traitToDimension[selectedTrait];
        
        // If we're only calculating for a specific dimension, skip others
        if (selectedDimension && dimension !== selectedDimension) return;
        
        // Count the trait chosen
        traitCounts[selectedTrait] = (traitCounts[selectedTrait] || 0) + 1;
        
        // Add to dimension score if the positive trait was chosen
        const positiveTraitForDimension = positiveTrait[dimension];
        if (selectedTrait === positiveTraitForDimension) {
          dimensionScores[dimension] += 1;
        }
      }
    });
    
    // Calculate consistency within each dimension
    Object.entries(dimensionGroups).forEach(([dimension, questionIds]) => {
      // If we're only calculating for a specific dimension, skip others
      if (selectedDimension && dimension !== selectedDimension) return;
      
      // Skip if dimension has less than 2 questions
      if (questionIds.length < 2) return;
      
      // Get positive and negative trait names for this dimension
      const positiveTrait = {
        'structureVsFlexibility': 'structure',
        'introvertVsExtrovert': 'introvert',
        'practicalVsConceptual': 'practical',
        'riskVsCaution': 'caution',
        'independentVsCollaborative': 'independent'
      }[dimension];
      
      const negativeTrait = {
        'structureVsFlexibility': 'flexibility',
        'introvertVsExtrovert': 'extrovert',
        'practicalVsConceptual': 'conceptual',
        'riskVsCaution': 'risk',
        'independentVsCollaborative': 'collaborative'
      }[dimension];
      
      // Count answers for each option using the traits
      const positiveTraitCount = traitCounts[positiveTrait] || 0;
      const negativeTraitCount = traitCounts[negativeTrait] || 0;
      
      // Calculate consistency as a percentage
      const totalAnswered = positiveTraitCount + negativeTraitCount;
      if (totalAnswered === 0) return;
      
      const dominantOption = Math.max(positiveTraitCount, negativeTraitCount);
      const consistencyPercentage = (dominantOption / totalAnswered) * 100;
      dimensionConsistency[dimension] = consistencyPercentage;
    });
    
    // Calculate total consistency and final score
    let totalConsistency = 0;
    let dimensionCount = 0;
    
    Object.values(dimensionConsistency).forEach(consistency => {
      if (consistency > 0) {
        totalConsistency += consistency;
        dimensionCount++;
      }
    });
    
    const averageConsistency = dimensionCount > 0 ? totalConsistency / dimensionCount : 0;
    
    // Calculate total raw score
    const rawScore = Object.values(dimensionScores).reduce((sum, score) => sum + score, 0);
    
    // Adjust score based on consistency
    // If 100% consistent, score is unchanged
    // If 50% consistent (random), score is halved
    const consistencyMultiplier = averageConsistency / 100;
    
    // For full test, maxPossibleScore is all questions
    // For single dimension, maxPossibleScore is just the questions for that dimension
    const maxPossibleScore = selectedDimension 
      ? dimensionGroups[selectedDimension].length 
      : questions.length;
    
    const finalScore = Math.round(rawScore * consistencyMultiplier);
    const scorePercentage = Math.round((finalScore / maxPossibleScore) * 100);
    
    return {
      dimensionScores,
      dimensionConsistency,
      rawScore,
      averageConsistency,
      finalScore,
      maxPossibleScore,
      scorePercentage,
      dimensionGroups,
      traitCounts,
      selectedDimension // Include info about whether this was a single dimension test
    };
  };

  // Check if all questions have been answered
  const activeQuestions = selectedDimension ? filteredQuestions : questions;
  const allQuestionsAnswered = activeQuestions.every(q => answers[q.id] !== undefined);
  const answeredCount = activeQuestions.filter(q => answers[q.id] !== undefined).length;

  // Show results if the test is complete
  if (showResults) {
    return <ResultsScreen results={results} />;
  }

  // Show dimension selector if we're at that stage
  if (showDimensionSelector) {
    return (
      <DimensionSelector 
        onSelectDimension={handleDimensionSelect}
        onStartFullTest={handleStartFullTest}
      />
    );
  }

  // Exit confirmation modal
  const ExitConfirmationModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 transform transition-all">
        <h3 className="text-lg font-bold text-gray-900 mb-3">Kembali ke Pemilihan Dimensi?</h3>
        <p className="text-gray-600 mb-6">
          Jawaban Anda akan tersimpan dan Anda dapat melanjutkan tes ini nanti. Yakin ingin kembali ke menu pemilihan dimensi?
        </p>
        <div className="flex justify-end space-x-3">
          <button
            onClick={() => setIsExitModalOpen(false)}
            className="px-4 py-2 rounded bg-gray-200 text-gray-800 font-medium hover:bg-gray-300 transition-colors"
          >
            Batal
          </button>
          <button
            onClick={() => {
              setIsExitModalOpen(false);
              setShowDimensionSelector(true);
            }}
            className="px-4 py-2 rounded bg-blue-600 text-white font-medium hover:bg-blue-700 transition-colors"
          >
            Ya, Kembali
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-100 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl mx-auto">
        <header className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900">
            {selectedDimension ? `Tes ${dimensionGroupToTitle(selectedDimension)}` : 'Tes Learning Agility BUMN'}
          </h1>
          <p className="mt-2 text-lg text-gray-600">
            Pilih pernyataan yang paling sesuai dengan pengalaman pribadi Anda
          </p>
        </header>

        <ProgressBar 
          current={currentIndex + 1} 
          total={activeQuestions.length} 
          answeredCount={answeredCount}
        />

        <QuestionCard
          question={activeQuestions[currentIndex]}
          selectedOption={answers[activeQuestions[currentIndex].id]}
          onAnswer={handleAnswer}
        />

        <div className="flex justify-between mt-6">
          <button
            onClick={handlePrevious}
            disabled={currentIndex === 0}
            className={`px-6 py-2.5 rounded-md font-medium flex items-center transition-colors ${
              currentIndex === 0 
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300 shadow-sm'
            }`}
          >
            <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Sebelumnya
          </button>
          
          {currentIndex === activeQuestions.length - 1 ? (
            <button
              onClick={handleSubmit}
              disabled={!allQuestionsAnswered}
              className={`px-6 py-2.5 rounded-md font-medium flex items-center transition-colors ${
                !allQuestionsAnswered 
                  ? 'bg-blue-300 text-white cursor-not-allowed' 
                  : 'bg-blue-600 text-white hover:bg-blue-700 shadow-sm'
              }`}
            >
              Selesai
              <svg className="w-5 h-5 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </button>
          ) : (
            <button
              onClick={() => setCurrentIndex(prev => prev + 1)}
              className="px-6 py-2.5 rounded-md bg-blue-600 text-white font-medium hover:bg-blue-700 transition-colors shadow-sm flex items-center"
            >
              Selanjutnya
              <svg className="w-5 h-5 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          )}
        </div>

        <div className="mt-8 text-center">
          <button
            onClick={() => setIsExitModalOpen(true)}
            className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center justify-center mx-auto"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 17l-5-5m0 0l5-5m-5 5h12" />
            </svg>
            Kembali ke Pemilihan Dimensi
          </button>
        </div>
      </div>

      {isExitModalOpen && <ExitConfirmationModal />}
    </div>
  );
}

// Helper function to convert dimension ID to readable title
function dimensionGroupToTitle(dimensionId) {
  const titles = {
    'structureVsFlexibility': 'Struktur vs Fleksibilitas',
    'introvertVsExtrovert': 'Introvert vs Ekstrovert',
    'practicalVsConceptual': 'Praktis vs Konseptual',
    'riskVsCaution': 'Pengambil Risiko vs Kehati-hatian',
    'independentVsCollaborative': 'Mandiri vs Kolaboratif'
  };
  
  return titles[dimensionId] || dimensionId;
}

export default App;